import type { Bulletin } from '@shared/types/Bulletin';
import { calcFGTLS, TAUX_CNAMGS_EMP, TAUX_CNAMGS_FAM_EMP } from '@shared/constants/Taux';
import { defaultParametresFiscauxPaie } from './ParametresFiscauxPaie';
import { ventilerCnssPatronale } from './VentilationCnss';
import { montantCotisation, roundFCFA } from '@shared/utils/round';

export interface DetailChargesPatronales {
  base_cnss: number;
  base_cnamgs: number;
  /** CNSS patronale totale (18 % = prestations familiales 5 % + AT-MP 2 % + VID 11 %). */
  cnss: number;
  /** CNSS patronale : prestations familiales et maternité (5 %). */
  cnss_pf: number;
  /** CNSS patronale : accidents du travail et maladies professionnelles (2 %). */
  cnss_atmp: number;
  /** CNSS patronale : assurance vieillesse, invalidité et décès (11 %). */
  cnss_vid: number;
  cnamgs: number;
  cfp: number;
  fnh: number;
  /** FGTLS 1 % sur brut imposable, sans plafond (Loi 3/94). */
  fgtls: number;
  /** Somme des lignes (≠ taux global × base). */
  total: number;
  cout_employeur: number;
}

/**
 * Part patronale affichée / contrôlée : chaque ligne = base × taux, total = somme.
 * Aligné DTS : on n’applique pas 25,6 % d’un coup sur la base.
 */
export function detailChargesPatronales(b: Bulletin): DetailChargesPatronales {
  const base_cnss = roundFCFA(b.base_cnss ?? 0);
  const base_cnamgs = roundFCFA(b.base_cnamgs ?? 0);
  const brutImposable = roundFCFA(b.brut_imposable ?? 0);
  // CNSS patronale : total sur le taux global (arrondi une fois, comme le moteur) réparti 5 % / 2 % / 11 % (communiqué CNSS du
  // 12/01/2026). Même total que l'ancienne ligne unique à 18 %.
  const ventilationCnss = ventilerCnssPatronale(base_cnss);
  const cnss = ventilationCnss.total;
  const cnamgs =
    montantCotisation(base_cnamgs, TAUX_CNAMGS_EMP) +
    montantCotisation(base_cnamgs, TAUX_CNAMGS_FAM_EMP);
  // Contributions en attente (CFP, FNH, FGTLS) : montants figés sur le bulletin ; à défaut, paramètres
  // par défaut (désactivées = 0).
  const defauts = defaultParametresFiscauxPaie(b.periode);
  const cfp = b.cfp_emp ?? montantCotisation(base_cnss, defauts.tauxTfp);
  const fnh = b.fnh_emp ?? montantCotisation(base_cnss, defauts.tauxCds);
  const fgtls = b.fgtls_emp ?? calcFGTLS(brutImposable, defauts.tauxFgtls);
  const total = cnss + cnamgs + cfp + fnh + fgtls;
  const brutVerse = b.brut_total ?? b.brut_imposable ?? 0;
  return {
    base_cnss,
    base_cnamgs,
    cnss,
    cnss_pf: ventilationCnss.pf,
    cnss_atmp: ventilationCnss.atmp,
    cnss_vid: ventilationCnss.vid,
    cnamgs,
    cfp,
    fnh,
    fgtls,
    total,
    cout_employeur: roundFCFA(brutVerse + total),
  };
}
